<template>
  <div>
    <default-modal
      v-model="modalOpened"
      name="add-customer-modal"
      :autofocus="$refs.addCustomerForm"
      :successHandler="handleStoreCustomer"
      :title="title"
      width="sm"
      @hide="handleHide"
    >
      <component :is="formComponent" slot="content" v-model="formData" :action="action" ref="addCustomerForm"/>
    </default-modal>

    <!-- Add customer button -->
    <q-page-sticky position="bottom-right" :offset="[18, 18]">
      <q-fab
        color="primary"
        icon="add"
        direction="up"
      >
        <q-fab-action
          color="dark"
          icon="business"
          class="add-company-btn"
          @click="handleAddCustomer('company')"
        >
          <q-tooltip
            anchor="center right"
            self="center left"
            :offset="[10, 0]"
          >
            {{ $tc('company', 1) }}
          </q-tooltip>
        </q-fab-action>
        <q-fab-action
          color="dark"
          icon="person_add"
          class="add-person-btn"
          @click="handleAddCustomer('person')"
        >
          <q-tooltip
            anchor="center right"
            self="center left"
            :offset="[10, 0]"
          >
            {{ $tc('person', 1) }}
          </q-tooltip>
        </q-fab-action>
      </q-fab>
    </q-page-sticky>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import AddCompanyForm from 'components/Customers/Companies/AddCompanyForm.vue'
  import AddPersonForm from 'components/Customers/Persons/AddPersonForm.vue'
  import BankDetails from 'src/utils/SubResources/CustomerBankDetails'
  import Customer from 'src/models/customer'
  import CustomerAddress from 'src/utils/SubResources/CustomerAddress'
  import CustomerEmailAddress from 'src/utils/SubResources/CustomerEmailAddress'
  import CustomerPhoneNumber from 'src/utils/SubResources/CustomerPhoneNumber'
  import DefaultModal from 'components/Modal/DefaultModal.vue'
  import { Notify } from 'quasar'
  import SubResourceHelper from 'src/utils/SubResourceHelper'
  import changeCase from 'change-case-object'
  import cleanDeep from 'clean-deep'
  import { createErrorFromResponse } from 'src/utils/error'
  import { pick } from 'lodash'

  export default {
    $_veeValidate: {
      validator: 'new'
    },

    data () {
      return {
        modalOpened: false,
        formData: null,
        original: null,
        action: null,
        type: null,
        id: null
      }
    },

    computed: {
      ...mapGetters(['customerModalData']),

      title () {
        if (!this.action || !this.type) {
          return
        }

        return this.$t([this.action, this.type].join('_'))
      },

      formComponent () {
        switch (this.type) {
        case 'company':
          return 'addCompanyForm'
        case 'person':
          return 'addPersonForm'
        }
      },

      storeMethod () {
        const methods = {
          company: { create: this.createCompany, update: this.updateCompany },
          person: { create: this.createPerson, update: this.updatePerson }
        }

        const method = methods[this.type][this.action]
        if (!method) {
          throw new Error('Invalid action or type')
        }

        return method
      }
    },

    methods: {
      ...mapActions(['createCompany', 'createPerson', 'updateCompany', 'updatePerson', 'fetchCustomer', 'setOpenEditCustomerModal']),

      handleAddCustomer (type) {
        this.type = type
        this.action = 'create'

        if (this.$refs.addCustomerForm) {
          this.$refs.addCustomerForm.resetData()
        }

        // Open next tick, otherwise autofocus will not work
        this.$nextTick(() => {
          this.modalOpened = true
        })
      },

      handleStoreCustomer () {
        this.$validator.validateAll()
          .then(result => {
            if (result) {
              this.storeCustomer()
            }
          })
      },

      storeCustomer () {
        let data = { ...this.formData }
        const customerAddress = new CustomerAddress()

        data.addresses = data.addresses.filter(address => {
          return !(customerAddress.isNew(address) && customerAddress.isEmpty(address))
        })

        data = changeCase.snakeCase(data)
        let subResourcesFailed = false
        // If we're not updating, we need a promise which immediately resolves to continue storing the new customer
        let promise = Promise.resolve()

        if (this.action === 'update') {
          promise = this.storeSubResources()
            .catch(error => {
              subResourcesFailed = true

              createErrorFromResponse(error)
            })
        }

        promise
          .then(() => this.storeMethod(this.cleanData(data)))
          .then(() => {
            if (subResourcesFailed) {
              return
            }

            Notify.create({
              message: this.$t(`${this.type}_${this.action}d`),
              type: 'positive'
            })
          })
          .then(() => {
            this.modalOpened = subResourcesFailed
          })
          .catch(createErrorFromResponse)
      },

      storeSubResources () {
        const emails = new SubResourceHelper(this.original.emailAddresses, new CustomerEmailAddress(this.id, this.type))
        const addresses = new SubResourceHelper(this.original.addresses, new CustomerAddress(this.id, this.type))
        const phones = new SubResourceHelper(this.original.phoneNumbers, new CustomerPhoneNumber(this.id, this.type))
        const bankDetails = new SubResourceHelper(this.original.bankDetails, new BankDetails(this.id, this.type))

        return Promise.all([
          emails.diff(this.formData.emailAddresses),
          addresses.diff(this.formData.addresses),
          phones.diff(this.formData.phoneNumbers),
          bankDetails.diff(this.formData.bankDetails)
        ])
      },

      cleanData (data) {
        // When patching a customer we do not remove null values, otherwise we can not remove a company from a person.
        let cleanedData = this.action === 'create' ? cleanDeep(data) : cleanDeep(data, { nullValues: false })

        if (this.action === 'create') {
          return cleanedData
        }

        // ToDo: Not possible to reset accounts numbers du to them being an empty string when cleared
        return pick(
          cleanedData,
          'id',
          'company_id',
          'business_partner_type',
          'customer_number',
          'industry',
          'language',
          'name',
          'vat_number',
          'tax_number',
          'website',
          'department',
          'position',
          'salutation',
          'first_name',
          'last_name',
          'default_payment_deadline',
          'title',
          'accounts_receivable_number',
          'accounts_payable_number'
        )
      },

      open ({ type, id }) {
        this.$refs.addCustomerForm.resetData()
        this.setOpenEditCustomerModal({ type, id })
      },

      handleHide () {
        if (['create-person', 'create-company'].includes(this.$route.name)) {
          this.$router.push({ name: 'customers' })
        }
      }
    },

    watch: {
      customerModalData: {
        deep: true,
        handler: function (data) {
          this.id = data.id
          this.type = data.type
          this.action = 'update'

          this.fetchCustomer(this.id)
            .then(response => {
              this.original = new Customer(response.data.data[0])
              this.$refs.addCustomerForm.loadData(new Customer(response.data.data[0]))

              this.modalOpened = true
            })
        }
      }
    },

    components: {
      AddCompanyForm,
      AddPersonForm,
      DefaultModal
    }
  }
</script>
